import React, { useState } from "react";
import { motion } from "framer-motion";
import { Cloud, Cpu, Activity, Code, Database, GitBranch, Wrench, Layers } from "lucide-react";
import { SKILLS_CATEGORIZED } from "../data/portfolioData";

const CATEGORY_ICONS = {
  Programming: Code,
  Backend: Layers,
  Frontend: Code,
  Database: Database,
  Cloud: Cloud,
  DevOps: Cpu,
  Monitoring: Activity,
  "Version Control": GitBranch,
  "Tools & Scripting": Wrench,
};

function SkillLogoItem({ name, src }) {
  const [imgError, setImgError] = useState(false);

  if (imgError || !src) {
    return (
      <div className="w-10 h-10 rounded-xl bg-indigo-100 border border-indigo-300 flex items-center justify-center font-bold text-indigo-900 text-sm shadow-sm">
        {name.substring(0, 2).toUpperCase()}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      onError={() => setImgError(true)}
      className="w-10 h-10 object-contain filter drop-shadow-sm group-hover:scale-110 transition-transform duration-300"
    />
  );
}

export default function Skills() {
  const [selectedCategory, setSelectedCategory] = useState("All");

  const categories = ["All", ...SKILLS_CATEGORIZED.map((c) => c.category)];

  const filteredCategories =
    selectedCategory === "All"
      ? SKILLS_CATEGORIZED
      : SKILLS_CATEGORIZED.filter((c) => c.category === selectedCategory);

  return (
    <section id="skills" className="py-20 relative overflow-hidden bg-white border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-100 border border-indigo-200 text-indigo-900 text-xs font-mono font-bold shadow-sm"
          >
            <Cpu size={15} /> TECH STACK & TOOLING CATALOG
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900"
          >
            Technical Proficiency & <span className="gradient-text-indigo">Tool Stack</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-slate-800 text-base font-medium leading-relaxed"
          >
            Structured index of programming languages, frameworks, cloud platforms, databases, and DevOps tools.
          </motion.p>
        </div>

        {/* Category Filter Pills */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
          {categories.map((cat) => {
            const isSelected = selectedCategory === cat;
            return (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full text-xs font-bold transition-all duration-300 flex items-center gap-1.5 ${
                  isSelected
                    ? "bg-indigo-600 text-white shadow-md shadow-indigo-600/20 border border-indigo-500"
                    : "bg-slate-100 text-slate-800 hover:text-indigo-700 hover:bg-slate-200 border border-slate-300"
                }`}
              >
                {cat}
              </button>
            );
          })}
        </div>

        {/* Skill Groups Grid */}
        <div className="mt-14 space-y-12">
          {filteredCategories.map((group) => {
            const CategoryIcon = CATEGORY_ICONS[group.category] || Cpu;

            return (
              <motion.div
                key={group.category}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="space-y-6"
              >
                {/* Category Header */}
                <div className="flex items-center gap-3 border-b border-slate-200 pb-3">
                  <div className="p-2 rounded-xl bg-indigo-100 text-indigo-800 border border-indigo-200">
                    <CategoryIcon size={20} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">
                    {group.category}
                  </h3>
                  <span className="text-xs font-mono font-bold px-3 py-0.5 rounded-full bg-indigo-50 text-indigo-900 border border-indigo-200">
                    {group.skills.length} tools
                  </span>
                </div>

                {/* Skill Cards Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {group.skills.map((skill) => (
                    <div
                      key={skill.name}
                      className="group relative p-4 rounded-2xl bg-slate-50 border border-slate-200 hover:border-indigo-500 transition-all duration-300 hover:-translate-y-1.5 shadow-sm hover:shadow-md flex flex-col items-center text-center space-y-3 cursor-pointer overflow-hidden"
                    >
                      {/* Skill Logo */}
                      <SkillLogoItem name={skill.name} src={skill.logo} />

                      {/* Skill Name */}
                      <div className="font-extrabold text-sm text-slate-900 group-hover:text-indigo-700 transition-colors">
                        {skill.name}
                      </div>

                      {/* Tooltip Description */}
                      <p className="text-[11px] font-medium text-slate-700 leading-snug line-clamp-2">
                        {skill.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
